import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Page } from '@/components/common';
import { DonationGate } from '@/components/DonationGate';
import { useAuth } from '@/hooks/useAuth';
import { getNonprofits } from '@/resources/give-api.resource';

// Card (USD / Stripe) donations are disabled for now — the Stripe Connect onboarding
// and connected-account flows aren't finished. Crypto paths remain enabled. To re-enable
// card donations, add 'USD' back to this list (the GiftSummary/GiftConfirmation card code
// is left in place).
const COINS = ['ETH', 'USDC', 'GIVE'];

function GiftSelectInner() {
  const router = useRouter();
  const { fetcher } = useAuth();
  const [nonprofits, setNonprofits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [selectedSlug, setSelectedSlug] = useState('');
  const [giftOptionId, setGiftOptionId] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [coin, setCoin] = useState('ETH');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetcher(getNonprofits({ includeGiftOptions: true }));
        if (active) setNonprofits(res ?? []);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetcher]);

  const selected = useMemo(
    () => nonprofits.find((n) => n.slug === selectedSlug),
    [nonprofits, selectedSlug]
  );

  const selectedOption = useMemo(
    () => selected?.giftOptions?.find((o) => String(o.id) === String(giftOptionId)),
    [selected, giftOptionId]
  );

  const amount = selectedOption ? selectedOption.usdValue : Number(customAmount) || 0;
  const canContinue = selected && amount > 0;

  const handleContinue = () => {
    const params = new URLSearchParams({
      c: selected.slug,
      ci: String(selected.id),
      a: String(amount),
      g: selectedOption ? String(selectedOption.id) : '',
      gn: selectedOption ? selectedOption.name : 'Custom',
      coin,
    });
    router.push(`/gifts/summary?${params.toString()}`);
  };

  return (
    <Page>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Typography variant="h2">Make a Donation</Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">
            Unable to load nonprofits. Please try again later.
          </Typography>
        ) : (
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              padding: '24px',
            }}
          >
            <TextField
              select
              label="Nonprofit"
              value={selectedSlug}
              onChange={(e) => {
                setSelectedSlug(e.target.value);
                setGiftOptionId('');
              }}
            >
              {nonprofits.map((n) => (
                <MenuItem key={n.slug} value={n.slug}>
                  {n.name}
                </MenuItem>
              ))}
            </TextField>

            {selected?.giftOptions?.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '12px',
                }}
              >
                {selected.giftOptions.map((option) => (
                  <Card
                    key={option.id}
                    variant="outlined"
                    sx={{
                      borderColor:
                        String(option.id) === String(giftOptionId)
                          ? 'primary.main'
                          : undefined,
                      borderWidth: String(option.id) === String(giftOptionId) ? 2 : 1,
                    }}
                  >
                    <CardActionArea
                      onClick={() => {
                        setGiftOptionId(option.id);
                        setCustomAmount('');
                      }}
                    >
                      <CardContent>
                        <Typography variant="h4">{option.name}</Typography>
                        <Typography variant="body2">
                          ${Number(option.usdValue).toFixed(2)}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption">{option.description}</Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            )}

            <TextField
              label="Or enter a custom amount (USD)"
              type="number"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setGiftOptionId('');
              }}
              inputProps={{ min: 1, step: '0.01' }}
            />

            <TextField
              select
              label="Pay with"
              value={coin}
              onChange={(e) => setCoin(e.target.value)}
            >
              {COINS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c === 'USD' ? 'Card (USD)' : c}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                disabled={!canContinue}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Page>
  );
}

export default function GiftSelect() {
  return (
    <DonationGate>
      <GiftSelectInner />
    </DonationGate>
  );
}
